import type { ReactNode } from 'react';
import { Fragment } from 'react';

import classNames from 'classnames';

import { AIImpactTooltip } from './AIImpactTooltip';

const colorClasses = {
  'chart-normal-1': {
    header: 'vai-bg-chart-normal-1',
    rate: 'vai-text-chart-normal-1',
    details: 'vai-bg-chart-normal-1',
    numbers: 'vai-text-chart-shade-1',
  },
  'chart-normal-2': {
    header: 'vai-bg-chart-normal-2',
    rate: 'vai-text-chart-normal-2',
    details: 'vai-bg-chart-normal-2',
    numbers: 'vai-text-chart-shade-2',
  },
  'chart-normal-3': {
    header: 'vai-bg-chart-normal-3',
    rate: 'vai-text-chart-normal-3',
    details: 'vai-bg-chart-normal-3',
    numbers: 'vai-text-chart-shade-3',
  },
};

type ChartColorVariant = 'chart-normal-1' | 'chart-normal-2' | 'chart-normal-3';

interface Props {
  children?: ReactNode;
  colorVariant: ChartColorVariant;
  details?: Array<{
    value: number;
    label: string;
  }>;
  featuredRate?: {
    value: number;
    prefix: string;
    suffix: string;
  };
  title: string;
  type: 'patient-education' | 'case-acceptance' | 'treatment-planning';
}

export const AIImpactCard = ({
  children,
  colorVariant,
  details,
  featuredRate,
  title,
  type,
}: Props) => {
  const colors = colorClasses[colorVariant];

  return (
    <div className="vai-rounded-md vai-border vai-border-slate-200 vai-overflow-hidden vai-flex vai-flex-col">
      {/* Header */}
      <div
        className={classNames(
          'vai-p-2 vai-flex vai-gap-2 vai-items-center vai-justify-center',
          colors.header,
        )}
      >
        <p className="vai-font-inter vai-text-sm vai-text-white vai-font-bold vai-text-center">
          {title}
        </p>
        <AIImpactTooltip type={type} />
      </div>

      {/* TODO: Adjust styling here when different parts are not available */}
      {/* Main content */}
      <div className="vai-flex-1 vai-p-4 vai-grid vai-grid-rows-[1fr_1.5fr]">
        {/* Main rate */}
        {featuredRate && (
          <div>
            <p className="vai-font-inter vai-font-medium vai-text-sm vai-text-zinc-900 vai-text-center">
              {featuredRate.prefix}
            </p>
            <p
              className={classNames(
                'vai-font-inter vai-font-bold vai-text-[40px] vai-text-center',
                colors.rate,
              )}
            >
              {featuredRate.value}%
            </p>
            <p className="vai-font-inter vai-font-medium vai-text-sm vai-text-zinc-900 vai-text-center">
              {featuredRate.suffix}
            </p>
          </div>
        )}

        {/* Details */}
        {children}
        {details && (
          <div
            className={classNames(
              'vai-mt-4 vai-p-3 vai-rounded-md vai-bg-opacity-[.07]',
              colors.details,
            )}
          >
            <div className="vai-grid vai-grid-cols-[1fr_auto] vai-gap-x-2 vai-gap-y-3">
              {details.map((item, index) => (
                <Fragment key={index}>
                  <p
                    className={classNames(
                      'vai-font-inter vai-font-bold',
                      colors.numbers,
                    )}
                  >
                    {item.value}
                  </p>
                  <p className="vai-font-inter vai-font-sm vai-text-zinc-900">
                    {item.label}
                  </p>
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
